import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { useSelector } from "react-redux";
import { toastAlert } from "../../helpers/toastAlert";
import { formatDate } from "../../utils/commonutils";
import {
  deleteDocument,
  downloadDocumentFile,
  getRecordDocuments,
  uploadDocument,
} from "../../services/Documentservice";
import { FALLBACK_DOCUMENT_CATEGORY, FALLBACK_DOCUMENT_TYPE } from "../../utils/masterFallbacks";
import MasterSelect from "./MasterSelect";
import FilePreview, { fileKindIcon } from "./FilePreview";
import ConfirmDeleteModal from "./Confrimationmodal";
import {
  ATTACHMENT_ACCEPT,
  validateAttachmentFile,
} from "../../utils/attachmentUtils";

export default function AttachmentPanel({
  moduleName,
  recordId,
  customerId,
  title = "Attachments",
}) {
  const authData = useSelector((state) => state.authReducer);
  const props = { authData };
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("");
  const [type, setType] = useState("personal");
  const [selected, setSelected] = useState(null);
  const [deleteId, setDeleteId] = useState("");

  const load = async () => {
    if (!recordId && !customerId) return;
    setLoading(true);
    const res = await getRecordDocuments(props, { module: moduleName, recordId, customerId });
    const list = res?.data?.documents || [];
    setDocs(list);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [moduleName, recordId, customerId]);

  const handleUpload = async () => {
    if (!file) {
      toastAlert("Choose a file first", "error");
      return;
    }
    const validationError = validateAttachmentFile(file);
    if (validationError) {
      toastAlert(validationError, "error");
      return;
    }
    setUploading(true);
    const res = await uploadDocument(props, file, {
      customerId,
      type,
      category,
      module: moduleName,
      recordId,
    });
    setUploading(false);
    if (res?.status === 200 || res?.status === 201 || res?.data?.status) {
      toastAlert("File attached", "success");
      setFile(null);
      load();
    } else {
      toastAlert(res?.response?.data?.message || "Upload failed", "error");
    }
  };

  const handleDownload = async (doc) => {
    try {
      await downloadDocumentFile(
        doc._id,
        authData?.accessToken,
        doc.fileName || "document",
      );
      toastAlert("Download started", "success");
    } catch (err) {
      toastAlert(err?.message || "Download failed", "error");
    }
  };

  const handleDelete = async () => {
    await deleteDocument(props, deleteId);
    setDeleteId("");
    if (selected?._id === deleteId) setSelected(null);
    toastAlert("Attachment removed", "success");
    load();
  };

  if (!recordId && !customerId) {
    return (
      <Typography variant="body2" color="textSecondary">
        Save the record first, then attach files.
      </Typography>
    );
  }

  return (
    <div className="drawer-section">
      <h3 className="drawer-section-title">{title}</h3>
      <Box display="grid" gap={8} style={{ gridTemplateColumns: "1fr 1fr auto" }} mb={2}>
        <MasterSelect
          group="document_type"
          fallback={FALLBACK_DOCUMENT_TYPE}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />
        <MasterSelect
          group="document_category"
          fallback={FALLBACK_DOCUMENT_CATEGORY}
          value={category}
          placeholder="Category"
          onChange={(e) => setCategory(e.target.value)}
        />
        <Button
          type="button"
          component="label"
          variant="outlined"
          className="btn btn-border"
          style={{ minWidth: 120, textTransform: "none" }}
        >
          <UploadFileIcon fontSize="small" />
          &nbsp;{file ? file.name : "Choose file"}
          <input
            type="file"
            hidden
            accept={ATTACHMENT_ACCEPT}
            onChange={(e) => {
              const next = e.target.files?.[0] || null;
              e.target.value = "";
              if (!next) {
                setFile(null);
                return;
              }
              const validationError = validateAttachmentFile(next);
              if (validationError) {
                toastAlert(validationError, "error");
                setFile(null);
                return;
              }
              setFile(next);
            }}
          />
        </Button>
      </Box>
      <button
        type="button"
        className="btn btn-colored"
        disabled={uploading || !file}
        onClick={handleUpload}
        style={{ marginBottom: 12 }}
      >
        {uploading ? "Uploading..." : "Upload"}
      </button>
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={22} />
        </Box>
      ) : docs.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No attachments yet.
        </Typography>
      ) : (
        docs.map((doc) => (
          <Box
            key={doc._id}
            onClick={() => setSelected(doc)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.25,
              mb: 1,
              borderRadius: 1.5,
              border: selected?._id === doc._id ? "1px solid #1a3366" : "1px solid #e2e8f0",
              background: selected?._id === doc._id ? "#eef3fb" : "#fff",
              cursor: "pointer",
            }}
          >
            {fileKindIcon(doc)}
            <Box flex={1} minWidth={0}>
              <Typography fontWeight={600} noWrap>
                {doc.fileName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {doc.category || doc.type || "File"} · {formatDate(doc.createdAt)}
              </Typography>
            </Box>
            <Tooltip title="Download">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(doc);
                }}
              >
                <CloudDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteId(doc._id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))
      )}
      {selected && (
        <Box mt={2}>
          <Typography fontWeight={600} mb={1}>
            Preview
          </Typography>
          <FilePreview document={selected} />
        </Box>
      )}
      <ConfirmDeleteModal
        open={Boolean(deleteId)}
        onClose={() => setDeleteId("")}
        onConfirm={handleDelete}
        message="Remove this attachment?"
      />
    </div>
  );
}
